import './index.scss'
import {useEffect, useMemo, useState, useRef} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {FieldArray, Formik} from 'formik'
import Input from '../../core/input'
import {SimpleBlueButton} from '../../core/button'
import {addResourceSchema} from '../../../utils/validation'
import {addResource, getServices, setIsFirstResource} from '../../../stores/resources'
import ButtonLoader from '../../core/button/ButtonLoader'
import TextHelper, {trimStringValues} from '../../../utils/text'
import localStorageManager from '../../../utils/localStorageManager'
import localStorageKeys from '../../../utils/localStorageKeys'
import CustomSelect from '../../core/customSelect'
import {locationManager} from '../../../utils/locations'
import LimitManager from '../../../utils/limitManager'
import types from '../../../data/monitoringTypes.json'
import {SecondaryTrashIcon, TrashIcon} from '../../../icons'
import protocols from '../../../data/protocols.json'
import Customer from '../../../utils/customer'
import httpMethods from '../../../data/httpMethods.json'
import Textarea from '../../core/textarea'
import authMethods from '../../../data/authMethods.json'
import parser from 'cron-parser'
import moment from 'moment'
import Modal from '../../core/modal'
import IconCardSlider from '../../core/slider/IconCardSlider'
import LargeSwitch from '../../core/switch/LargeSwitch'
import {getAuthBearerTokenString, getAuthBasicValue} from '../../../utils/http'
import AuthMethods from './AuthMethods'
import {setSelectedTeamId} from '../../../stores/teams'
import regexPatterns from '../../../utils/regexPatterns'
import classNames from 'classnames'
import Checkbox from '../../core/checkbox'
import {colors} from '../../../utils/colors'
import {flattenErrors} from '../../../utils/forms'
import {useConditionManagement, useFocusBlur} from './UseConditionsHook'
import RenderedForPaid from '../RenderedForPaid'
import limitUsageManager from '../../../utils/hooks/limitUsageManager'
import {useNamespaceTranslations} from '../../../utils/hooks/useNamespacesTranslations'

const getAvailableOptions = (item, index, premiumText, usageLimits) => {
    if (index) {
        return {
            ...item,
            disabled: !LimitManager.getConditionMonitoringIsEnabled(usageLimits),
            label: LimitManager.getConditionMonitoringIsEnabled(usageLimits) ? item.label : `${item.label} ${premiumText}`
        }
    }
    return item
}

const VALUES = 'values'
const HTTP = 'http'
const GRACE_PERIOD_DEFAULT = 0
const RECOVERY_PERIOD_DEFAULT = 0

const names = {
    type: 'type',
    monitoring_protocol: 'monitoring_protocol',
    name: 'name',
    service_id: 'service_id',
    url: 'url',
    regions: 'regions',
    conditionOption: 'conditionOption',
    conditions: 'conditions',
    keyword: 'keyword',
    is_advanced: 'is_advanced',
    headers: 'headers',
    team_id: 'team_id',
    port: 'port',
    data: 'data',
    method: 'method',
    body: 'body',
    key: 'key',
    skip_ssl_error: 'skip_ssl_error',
    not_follow_redirect: 'not_follow_redirect',
    operator: 'operator',
    value: 'value',
    grace_period: 'grace_period',
    recovery_period: 'recovery_period',
    maintenance_cron_expression: 'maintenance_cron_expression',
    maintenance_duration: 'maintenance_duration'
}

const MIN_CONDITIONS_LENGTH = 1

const AddResourceModal = ({isOpen, title, closeModal, defaultResourceData}) => {
    const data = useNamespaceTranslations('resourcesData')
    const text = useNamespaceTranslations('resources')
    const usageLimits = useNamespaceTranslations('usage-limits')
    const conditionTypes = useNamespaceTranslations('condition-types')?.resource_conditions
    const defaultConditionOptions = useMemo(() => Object.values(conditionTypes), [conditionTypes])
    const resourcesDataValues = useMemo(() => Object.values(data.resource_types), [data.resource_types])
    const gracePeriodValues = useMemo(() => Object.values(text.grace_periods), [text.grace_periods])
    const recoveryPeriodValues = useMemo(() => Object.values(text.recovery_periods), [text.recovery_periods])

    const conditionPairs = {
        [conditionTypes.json_key_value_check.type]: [{key: '', operator: '', value: ''}],
        [conditionTypes.keyword_value_check.type]: [{key: '', operator: '', value: ''}],
        [conditionTypes.status_code_check.type]: [{key: '', operator: '', value: ''}]
    }

    const conditionOptions = useMemo(() => defaultConditionOptions?.map((item, index) => getAvailableOptions(item, index, text.premium, usageLimits)), [])
    const [defaultConditionOption] = conditionOptions
    const organisationLimitUsages = useSelector((state) => state.auth.organisationLimitUsages)
    const {
        value: resourceLimit,
        usage: resourceUsage,
        remainingUsageValue: resourceRemainingValue
    } = limitUsageManager(usageLimits.limit_modules.monitoring.module, usageLimits.limit_keys.resource_count.limit_key, organisationLimitUsages)

    const [buttonIsDisabled, setButtonIsDisabled] = useState(false)
    const [teamServices, setTeamServices] = useState([])
    const services = useMemo(() => {
        return localStorageManager.get('services') || '[]'
    }, [])
    const selectedTeamId = useSelector(state => state.teams.selectedTeamId)
    const isDarkMode = useSelector(state => state.sidebar.isDarkMode)
    const dispatch = useDispatch()
    const [active, setActive] = useState(null)
    const defaultHeaders = {key: '', value: ''}
    const teams = useSelector(state => state.teams.teams)
    const [nextTriggerDate, setNextTriggerDate] = useState('')
    const [cronInputIsValid, setCronInputIsValid] = useState(true)
    const formikRef = useRef(null)
    const [activeResourceKey, setActiveResourceKey] = useState(data?.resourceKeys?.uptime)
    const dateTimeUTCString = 'YYYY-MM-DD HH:mm:ss UTC'

    const hasAdminAccess  = Customer.isRoot() || Customer.isManager()
    const initialValues = {
        type: types.http.value,
        monitoring_protocol: null,
        name: '',
        conditionOption: defaultConditionOption,
        conditions: conditionPairs,
        keyword: '',
        service_id: '',
        url: '',
        regions: [],
        skip_ssl_error: false,
        not_follow_redirect: false,
        is_advanced: null,
        method: httpMethods.GET,
        body: '',
        headers: [defaultHeaders],
        team_id: teams.find(item => item.value === selectedTeamId),
        port: '',
        data: '',
        auth_method: null,
        bearer_token: '',
        username: '',
        password: '',
        grace_period: gracePeriodValues?.find(item => item?.value === GRACE_PERIOD_DEFAULT),
        recovery_period:  recoveryPeriodValues.find(item => item?.value === RECOVERY_PERIOD_DEFAULT),
        maintenance_cron_expression: '',
        maintenance_duration: ''
    }

    const {focusedInput, handleFocus, handleBlur} = useFocusBlur()
    const {handleConditionsInputChange, handleRemoveConditions, submitted, setSubmitted, keyCounts} = useConditionManagement(formikRef)

    const [initialFormValues, setInitialFormValues] = useState(initialValues)

    const userAddResource = (values, {setFieldError, resetForm}) => {
        const url = values.type === types.http.value ? TextHelper.normalizeUrl(values.url) : TextHelper.addPrefix(values.url, values?.monitoring_protocol?.value)
        const conditionType = values?.conditionOption?.type
        const updatedConditions = values?.conditions?.[conditionType]?.length > MIN_CONDITIONS_LENGTH ? values?.conditions?.[conditionType]?.filter(condition => condition?.key && condition?.value) : values?.conditions?.[conditionType]

        let formData = {
            ...values,
            url,
            conditions: updatedConditions?.map(conditions => trimStringValues(conditions)),
            regions: values.regions.map(item => item.value),
            service_id: values.service_id?.value,
            team_id: values?.team_id?.value,
            method: values?.method?.value,
            type: values?.monitoring_protocol?.value || values?.type,
            port: values?.port ? String(values?.port) : null,
            grace_period: values?.grace_period?.value,
            recovery_period: values?.recovery_period?.value
        }
        delete formData.monitoring_protocol

        if (values.is_advanced) {
            const headers = {}
            values.headers?.forEach(item => {
                if (item.key && item.value) {
                    headers[item.key] = item.value
                }
            })
            if (values.auth_method?.value === authMethods.bearer_token.value) {
                headers['Authorization'] = getAuthBearerTokenString(values.bearer_token)
            }
            if (values.auth_method?.value === authMethods.basic_auth.value) {
                headers['Authorization'] = getAuthBasicValue(values.username, values.password)
            }
            formData = {
                ...formData,
                headers: JSON.stringify(headers),
                maintenance_cron_expression: values?.maintenance_cron_expression,
                maintenance_duration: parseInt(values?.maintenance_duration)
            }
        }
        if (!values.is_advanced) {
            formData = {
                ...formData,
                headers: null,
                maintenance_cron_expression: null,
                maintenance_duration: null
            }
        }
        delete formData.auth_method
        delete formData.bearer_token
        delete formData.username
        delete formData.password

        dispatch(addResource(trimStringValues(formData), setFieldError, resetForm, setButtonIsDisabled))
        if (localStorageManager.get(localStorageKeys.NO_RESOURCE_CREATED_KEY)) {
            localStorage.setItem(localStorageKeys.SHOW_STATISTICS_TIPS, true)
            setTimeout(() => {
                dispatch(setIsFirstResource(true))
            }, 10000)
        }
        setSubmitted(formData?.conditions || [])
    }

    const handleInfo = (index, state) => {
        state ? setActive(index) : setActive(null)
    }

    const getLimitIsExceeded = () => {
        return resourceUsage >= resourceLimit
    }

    const isConditionType = type => {
        return type !== conditionTypes?.becomes_unavailable?.value
    }

    const getPaidText = () => {
        return resourceRemainingValue > 0 && TextHelper.replaceAllChars(
            TextHelper.replaceAllChars(
                text.for_paid, ':remaining', resourceRemainingValue
            ),
            [':limit', ':s'],
            [resourceLimit, TextHelper.pluralize(resourceLimit)]
        )
    }

    const checkInputs = (data, push) => {
        const hasKey = data.every(item => item?.key)
        const hasValue = data.every(item => item?.value)
        if (hasKey || hasValue) {
            push(defaultHeaders)
        }
    }

    let headerPusher
    const generateJSONFields = (values, handleChange, errors) => {
        const headers = values.headers?.length ? values.headers : [defaultHeaders]
        return <FieldArray name={names.headers}>
            {({push, remove}) => {
                headerPusher = push
                return (
                    headers?.map((item, index) => (
                        <div className={classNames('json-box', {
                            dark: isDarkMode
                        })} key={index}>
                            <div className="json-box-header">
                                <span className="title">{text.header}</span>
                                {index > 0 && <span className={classNames('delete', {
                                    dark: isDarkMode
                                })} onClick={() => remove(index)}>
                                    <TrashIcon/>
                                </span>}
                            </div>
                            <div className="json-box-content simple-headers">
                                <Input
                                    onChange={e => {
                                        handleChange(e)
                                        checkInputs(headers, push)
                                    }}
                                    error={errors?.headers?.[index]?.key}
                                    name={`headers.${index}.key`}
                                    placeholder={text.key}
                                    value={item?.key}
                                    inputIsDisabled={!values[names.is_advanced]}
                                />
                                <Input
                                    onChange={e => {
                                        handleChange(e)
                                        checkInputs(headers, push)
                                    }}
                                    error={errors?.headers?.[index]?.value}
                                    name={`headers.${index}.value`}
                                    value={item?.value}
                                    placeholder={text.value}
                                    inputIsDisabled={!values[names.is_advanced]}/>
                            </div>
                        </div>
                    ))
                )
            }}
        </FieldArray>
    }

    const handleCronInputChange = (e, handleChange) => {
        handleChange(e)
        const interval = parser.parseExpression(e.target.value)
        if (regexPatterns.cron.test(e.target.value)) {
            setNextTriggerDate(moment(interval.next().toString()).format(dateTimeUTCString))
            setCronInputIsValid(true)
            return
        }
        setNextTriggerDate('')
        setCronInputIsValid(false)
    }

    const generateMaintenanceField = (values, handleChange, errors) => {
        return (
            <div className={classNames('json-box', {
                dark: isDarkMode
            })}>
                <div className="json-box-header">
                    <span className="title">{text.maintenance}</span>
                </div>
                <div className="json-box-content maintenance">
                    <Input
                        onChange={e => {
                            handleCronInputChange(e, handleChange)
                        }}
                        error={errors?.maintenance_cron_expression}
                        name={names.maintenance_cron_expression}
                        placeholder={text.cron_expression}
                        value={values.maintenance_cron_expression}
                        handleInfo={(state) => handleInfo(names.maintenance_cron_expression, state)}
                        infoText={text.cron_expression_info}
                        isValid={cronInputIsValid}
                        inputIsDisabled={!values[names.is_advanced]}
                    />
                    {nextTriggerDate && (
                        <span className={classNames('next', {
                            dark: isDarkMode
                        })}>{`${text.next} ${nextTriggerDate}`}</span>
                    )}
                    <Input
                        onChange={handleChange}
                        error={errors?.maintenance_duration}
                        name={names.maintenance_duration}
                        placeholder={text.duration}
                        value={values.maintenance_duration}
                        handleInfo={(state) => handleInfo(names.maintenance_duration, state)}
                        infoText={text.duration_info}
                        inputIsDisabled={!values[names.is_advanced]}
                    />
                </div>
            </div>
        )
    }

    const handleChangeBody = (e, handleChange, setValue, values) => {
        handleChange(e)
        const hasContentType = values?.headers?.some(item => item?.value === data.content_type.values.appliaction_json.key && item?.key === data.content_type.key)
        if (hasContentType) {
            return
        }

        try {
            JSON.parse(e.target.value)
            let emptyHeaderIndex = values?.headers?.findIndex(item => !item?.value && !item?.key)
            emptyHeaderIndex = emptyHeaderIndex > -1 ? emptyHeaderIndex : 0
            setValue(`headers.${emptyHeaderIndex}.key`, data.content_type.key)
            setValue(`headers.${emptyHeaderIndex}.value`, data.content_type.values.appliaction_json.key)
            headerPusher(defaultHeaders)
        } catch (e) {
            return false
        }
    }

    const generateMethods = (values, handleChange, setFieldValue, errors) => {
        return (
            <>
                <CustomSelect
                    options={Object.values(httpMethods)}
                    onChange={(name, data) => {
                        setFieldValue(name, data)
                        setFieldValue(names.body, initialValues.body)
                    }}
                    placeholder={text.method}
                    value={values.method}
                    error={errors.method}
                    name={names.method}
                    activeInfo={active === names.method}
                    handleInfo={(state) => handleInfo(names.method, state)}
                    infoText={text.method_info}
                    isDisabled={!values[names.is_advanced]}
                />
                {values.method?.hasPayload && (
                    <Textarea
                        value={values.body}
                        onChange={e => handleChangeBody(e, handleChange, setFieldValue, values)}
                        name={names.body}
                        error={errors.body}
                        placeholder={text.body}
                        activeInfo={active === names.body}
                        handleInfo={(state) => handleInfo(names.body, state)}
                        infoText={text.body_info}
                        isDisabled={!values[names.is_advanced]}
                    />
                )}
            </>
        )
    }

    const generateKeywordFields = (values, errors) => {
        const {values: options, type} = conditionTypes.keyword_value_check

        return (
            <div className='checker-wrapper'>
                {values.conditions?.[type].map((condition, index) =>
                    <>
                        <Input
                            type="text"
                            value=""
                            placeholder={text.keyword}
                            name={names.key}
                            error={errors?.conditions?.[type]?.[index]?.key}
                            inputIsDisabled
                            activeInfo={active === names.key}
                            handleInfo={(state) => handleInfo(names.key, state)}
                        />
                        <CustomSelect
                            options={Object.values(options)}
                            name={names.operator}
                            placeholder={text.operator}
                            onChange={(_, data) => handleConditionsInputChange(index, names.operator, data)}
                            isClearable
                            error={errors?.conditions?.[type]?.[index]?.operator || errors?.[type]}
                            value={condition.operator}
                            activeInfo={active === names.operator}
                            handleInfo={(state) => handleInfo(names.operator, state)}
                            isDisabled={getLimitIsExceeded()}
                        />
                        <Input
                            type="text"
                            value={condition.value}
                            infoText={text.keyword_info}
                            placeholder={text.value}
                            name={names.value}
                            error={errors?.conditions?.[type]?.[index]?.value}
                            onChange={(e) => handleConditionsInputChange(index, names.value, e.target.value)}
                            activeInfo={active === names.value}
                            handleInfo={(state) => handleInfo(names.value, state)}
                            inputIsDisabled={getLimitIsExceeded()}
                        />
                    </>)}
            </div>
        )
    }

    const generateStatusCodeFields = (values, errors) => {
        const {values: options, type} = conditionTypes.status_code_check

        return (
            <div className='checker-wrapper'>
                {values.conditions?.[type].map((condition, index) =>
                    <>
                        <Input
                            type="text"
                            value=""
                            placeholder={text.status_code_name}
                            name={names.key}
                            error={errors?.conditions?.[type]?.[index]?.key}
                            inputIsDisabled
                            activeInfo={active === names.key}
                            handleInfo={(state) => handleInfo(names.key, state)}
                        />
                        <CustomSelect
                            options={Object.values(options)}
                            name={names.operator}
                            placeholder={text.operator}
                            onChange={(_, data) => handleConditionsInputChange(index, names.operator, data)}
                            isClearable
                            error={errors?.conditions?.[type]?.[index]?.operator || errors?.[type]}
                            value={condition.operator}
                            activeInfo={active === names.operator}
                            handleInfo={(state) => handleInfo(names.operator, state)}
                            isDisabled={getLimitIsExceeded()}
                        />
                        <Input
                            type="text"
                            value={condition.value}
                            infoText={text.status_code_info}
                            placeholder={text.status_code}
                            name={names.value}
                            error={errors?.conditions?.[type]?.[index]?.value}
                            onChange={(e) => handleConditionsInputChange(index, names.value, e.target.value)}
                            activeInfo={active === names.value}
                            handleInfo={(state) => handleInfo(names.value, state)}
                            inputIsDisabled={getLimitIsExceeded()}
                        />
                    </>
                )}
            </div>
        )
    }

    const generateStandardJsonFields = (values, errors) => {
        const {values: options, type} = conditionTypes.json_key_value_check
        const conditions = values?.conditions?.[type]

        const modifiedErrors = errors?.conditions?.[type] ? errors : flattenErrors(errors)

        return (
            <>
                {conditions.map((condition, index) => {
                    const isFocused = name => focusedInput?.[index] === name
                    const errorIndex = errors?.conditions?.[type] ? index : conditions.slice(0, index).filter(c => c.key === condition.key).length
                    const foundCondition = submitted?.find((_, i) => i === index)
                    const isSameLength = modifiedErrors?.[foundCondition?.key]?.length === keyCounts[foundCondition?.key]

                    return <div className={`checker-wrapper ${focusedInput?.[index] ? 'focused-container' : ''}`}>
                        <Input
                            type="text"
                            value={condition?.key}
                            placeholder={text.key}
                            name={names.key}
                            infoText={index === 0 && isFocused(names.key) && text.json_info}
                            customIcon={index !== 0 && isFocused(names.key) && <SecondaryTrashIcon stroke={colors.colorRed}/>}
                            containerClassName={!isFocused(names.key) ? 'no-focused' : ''}
                            error={modifiedErrors?.conditions?.[type]?.[errorIndex]?.key}
                            onChange={(e) => handleConditionsInputChange(index, names.key, e.target.value, modifiedErrors, true)}
                            activeInfo={active === names.key}
                            handleInfo={(state) => handleInfo(names.key, state)}
                            inputIsDisabled={getLimitIsExceeded()}
                            onFocus={() => handleFocus(names.key, index)}
                            onBlur={handleBlur}
                        />
                        <CustomSelect
                            options={Object.values(options)}
                            name={names.operator}
                            placeholder={text.operator}
                            onChange={(_, data) => handleConditionsInputChange(index, names.operator, data, true)}
                            isClearable
                            error={modifiedErrors?.conditions?.[type]?.[errorIndex]?.operator || (isSameLength && modifiedErrors?.[foundCondition?.key]?.[errorIndex])}
                            value={condition?.operator}
                            activeInfo={active === names.operator}
                            handleInfo={(state) => handleInfo(names.operator, state)}
                            isDisabled={getLimitIsExceeded()}
                        />
                        <Input
                            type="text"
                            value={condition?.value}
                            containerClassName={!isFocused(names.value) ? 'no-focused' : ''}
                            infoText={index === 0 && text.json_info}
                            customIcon={index !== 0 && <SecondaryTrashIcon stroke={colors.colorRed}/>}
                            customIconOnclick={() => handleRemoveConditions(condition, errorIndex, index, modifiedErrors)}
                            placeholder={text.value}
                            name={names.value}
                            error={modifiedErrors?.conditions?.[type]?.[errorIndex]?.value}
                            onChange={(e) => handleConditionsInputChange(index, names.value, e.target.value, true)}
                            activeInfo={active === names.value}
                            handleInfo={(state) => handleInfo(names.value, state)}
                            inputIsDisabled={getLimitIsExceeded()}
                            onFocus={() => handleFocus(names.value, index)}
                            onBlur={handleBlur}
                        />
                    </div>
                }
                )}
            </>
        )
    }

    const getCurrentFields = (values, errors) => {
        if (values.conditionOption?.value === conditionTypes?.keyword_value_check?.value) {
            return generateKeywordFields(values, errors)
        }
        if (values.conditionOption?.value === conditionTypes?.status_code_check?.value) {
            return generateStatusCodeFields(values, errors)
        }
        if (values.conditionOption?.value === conditionTypes?.json_key_value_check?.value) {
            return generateStandardJsonFields(values, errors)
        }
    }

    const handleChangeUrl = (e, values, touched, handleChange, setFieldValue) => {
        handleChange(e)
        const normalizedUrl = TextHelper.getNormalizedUrl(e.target.value)
        if (!values.resource_name && values[names.type] === types.http.value) {
            setFieldValue(names.url, normalizedUrl)
        }

        if ((!touched[names.name] || !values[names.name]) && !(regexPatterns.ipv6.test(e.target.value))) {
            setFieldValue(names.name, TextHelper.generateNameFromUrl(normalizedUrl))
        }
    }

    const handleAddResourceType = (item, names, reset, setFieldValue, setErrors, values) => {
        let conditionOption = ''
        reset({
            values: {
                ...initialFormValues,
                team_id: values.team_id,
                service_id: values.service_id || initialFormValues.service_id,
                conditions: values.conditions
            }
        })
        setFieldValue(names.type, item?.value)
        setErrors({})
        if ([types.http.value, types.port.value].includes(item.value)) {
            conditionOption = defaultConditionOption
            setFieldValue(names.conditionOption, conditionOption)
            return
        }
        setFieldValue(names.conditionOption, '')
    }

    const handleChangeResource = (monitoringType, conditionType, resourceKey) => {
        const {setFieldValue, resetForm, setErrors, values} = formikRef.current
        const monitoringTypes = Object.values(types).filter(monitoringTypeItem => !monitoringTypeItem?.monitoring_protocol)
        const currentMonitoringType = monitoringTypes.find(monitoringTypeItem => monitoringTypeItem.value === monitoringType)
        const currentConditionOption = conditionOptions.find(conditionTypeItem => conditionTypeItem.value === conditionType)

        if (currentMonitoringType) handleAddResourceType(currentMonitoringType, names, resetForm, setFieldValue, setErrors, values)

        if (currentMonitoringType?.value === types.port.value) {
            if (resourceKey === protocols.port.data.udp.value) {
                setFieldValue(names.monitoring_protocol, protocols.port.data.udp)
            }
            if (resourceKey === protocols.port.data.tcp.value) {
                setFieldValue(names.monitoring_protocol, protocols.port.data.tcp)
            }
        }

        setActiveResourceKey(resourceKey)

        if (currentConditionOption) {
            setFieldValue(names.conditionOption, currentConditionOption)
            const updatedConditions = values?.conditions?.[currentConditionOption?.type]?.map(conditionPair => ({...conditionPair,
                operator: !conditionPair.operator ?
                    Object.values(currentConditionOption?.values)?.[0] :
                    conditionPair.operator
            }))
            setFieldValue(names.conditions, ({...values.conditions, [currentConditionOption?.type]: updatedConditions}))
        }
    }

    const [defaultResourceSetted, setDefaultResourceSetted] = useState(false)

    useEffect(() => {
        setActiveResourceKey(data?.resourceKeys?.uptime)
    }, [])

    useEffect(() => {
        if (defaultResourceData && formikRef.current && !defaultResourceSetted) {
            handleChangeResource(defaultResourceData.monitoring_type, defaultResourceData.condition_type, defaultResourceData.resource_key)
            setDefaultResourceSetted(true)
        }
    }, [defaultResourceData, formikRef.current])

    useEffect(() => {
        if (services.length) {
            setTeamServices(services)
            setInitialFormValues(prev => ({
                ...prev,
                service_id: services[0]
            }))
        }
    }, [services])

    useEffect(() => {
        const customerCountry = localStorageManager.get('customer')?.country
        if (customerCountry) {
            setInitialFormValues(prev => ({
                ...prev,
                regions: [locationManager.getRegionByCustomerCountryCode(customerCountry)]
            }))
            return
        }
        setInitialFormValues(prev => ({
            ...prev,
            regions: [locationManager.getDefaultRegion()]
        }))
    }, [isOpen])

    const getConditionOptions = type => {
        if (type === types.port.value) {
            return [conditionTypes?.becomes_unavailable, conditionTypes?.does_contain, conditionTypes?.does_not_contain]
        }
        return conditionOptions
    }

    const hasPort = values => [types.port.value, types.email_protocols.value].includes(values[names.type])
    const isHttpMonitoring = values => [types.http.value].includes(values[names.type])
    const isPortMonitoring = values => types.port.value === values.type
    const isTabDisabled = type => hasPort({type}) && !LimitManager.getPortMonitoringIsEnabled(usageLimits)

    return (
        <Modal
            contentClassName="resource-modal-content"
            title={title}
            isOpen={isOpen}
            closeModal={closeModal}>
            <div className='resource-modal-body'>
                <RenderedForPaid
                    forPaidText={getPaidText() || text.for_paid}
                    isExceeded={getLimitIsExceeded()}
                    className="payment-alert"
                    isTextWithLimits={!!getPaidText()} />
                <div className="modal-title">{text.resource_types}</div>
                <Formik
                    enableReinitialize={true}
                    initialValues={initialFormValues}
                    onSubmit={userAddResource}
                    validationSchema={() => addResourceSchema(conditionTypes)}
                    validateOnBlur={false}
                    validateOnChange={false}
                    innerRef={formikRef}
                >
                    {({values, errors, touched, handleChange, handleSubmit, setFieldValue, setFieldError, setFieldTouched}) => {
                        return (
                            <>
                                <IconCardSlider
                                    mustSlideToStartOnChange={false}
                                    slides={
                                        resourcesDataValues
                                            ?.filter(resource => !resource?.disabled)
                                            ?.map(resource => {
                                                const currentConditionOption = conditionOptions.find(conditionOption => conditionOption.value === resource.condition_type)
                                                const isActive = resource.key === activeResourceKey
                                                return {
                                                    ...resource,
                                                    disabled: getLimitIsExceeded() || isTabDisabled(resource.monitoring_type) || currentConditionOption?.disabled,
                                                    icon: resource.icon,
                                                    active: isActive,
                                                    onClick: () => handleChangeResource(resource.monitoring_type, resource.condition_type, resource.key)
                                                }
                                            })
                                    }
                                />
                                <div className="modal-title">{text.resource_details}</div>
                                <form onSubmit={handleSubmit}>
                                    <div className='form-container'>
                                        <div className="standard-form">
                                            <Input
                                                type="text"
                                                value={values.url}
                                                infoText={text.url_info}
                                                placeholder={values[names.type] === types.http.value ? text.url : text.host}
                                                name={names.url}
                                                error={errors.url}
                                                onChange={e => handleChangeUrl(e, values, touched,  handleChange, setFieldValue)}
                                                activeInfo={active === names.url}
                                                handleInfo={(state) => handleInfo(names.url, state)}
                                                inputIsDisabled={getLimitIsExceeded()}
                                            />
                                            {values.type === HTTP &&
                                            <>
                                                <Checkbox
                                                    type="checkbox"
                                                    id="skip-ssl-error-checkbox"
                                                    checked={!values.skip_ssl_error}
                                                    infoText={text.skip_ssl_validation_info}
                                                    label={text.skip_ssl_validation_label}
                                                    onChange={() => setFieldValue(names.skip_ssl_error, !values.skip_ssl_error)}
                                                />
                                                <Checkbox
                                                    type="checkbox"
                                                    id="follow-redirect-checkbox"
                                                    checked={!values.not_follow_redirect}
                                                    infoText={text.follow_redirect_info}
                                                    label={text.follow_redirect_label}
                                                    onChange={() => setFieldValue(names.not_follow_redirect, !values.not_follow_redirect)}
                                                />
                                            </>
                                            }
                                            {values.conditionOption.value && values.type !== types.icmp.value && !(VALUES in values.conditionOption) && isConditionType(values.conditionOption.value) &&
                                                <Input
                                                    type="text"
                                                    value={values.keyword}
                                                    placeholder={text.keyword}
                                                    infoText={values.conditionOption.inputInfoText}
                                                    name={names.keyword}
                                                    error={errors.keyword}
                                                    onChange={e => {
                                                        handleChange(e)
                                                    }}
                                                    activeInfo={active === names.keyword}
                                                    handleInfo={(state) => handleInfo(names.keyword, state)}
                                                />}
                                            {values.conditionOption && values.type === types.port.value && <CustomSelect
                                                options={getConditionOptions(values?.type)}
                                                value={values.conditionOption}
                                                infoText={values.conditionOption.selectInfoText}
                                                placeholder={text.condition_option}
                                                name={names.conditionOption}
                                                error={errors.conditionOption}
                                                onChange={(name, data) => {
                                                    setFieldValue(name, data)
                                                    setFieldValue(names.operator, null)
                                                    setFieldValue(names.key, '')
                                                    setFieldValue(names.value, '')
                                                }}
                                                activeInfo={active === names.conditionOption}
                                                handleInfo={(state) => handleInfo(names.conditionOption, state)}
                                                isDisabled={getLimitIsExceeded()}
                                            />}
                                            {hasPort(values) && (
                                                <>
                                                    {values.type === types.email_protocols.value &&
                                                        <CustomSelect
                                                            options={Object.values(protocols[isPortMonitoring(values) ? protocols.port.key : protocols.smtp_imap_pop3.key].data)}
                                                            value={values.monitoring_protocol}
                                                            infoText={isPortMonitoring(values) ? text.protocol_info : text.email_protocol_info}
                                                            placeholder={text.select_protocol}
                                                            name={names.monitoring_protocol}
                                                            error={errors.monitoring_protocol}
                                                            onChange={setFieldValue}
                                                            activeInfo={active === names.monitoring_protocol}
                                                            handleInfo={(state) => handleInfo(names.monitoring_protocol, state)}
                                                            isDisabled={getLimitIsExceeded()}
                                                        />
                                                    }
                                                    <Input
                                                        type="number"
                                                        value={values.port}
                                                        placeholder={text.port}
                                                        infoText={text.port_info}
                                                        name={names.port}
                                                        error={errors.port}
                                                        onChange={handleChange}
                                                        activeInfo={active === names.port}
                                                        handleInfo={(state) => handleInfo(names.port, state)}
                                                    />
                                                    {isPortMonitoring(values) && (
                                                        <Textarea
                                                            value={values.data}
                                                            placeholder={text.data}
                                                            infoText={text.data_info}
                                                            name={names.data}
                                                            error={errors.data}
                                                            onChange={handleChange}
                                                            activeInfo={active === names.data}
                                                            handleInfo={(state) => handleInfo(names.data, state)}
                                                        />
                                                    )}
                                                </>
                                            )}
                                            {values?.conditionOption && VALUES in values.conditionOption &&
                                                getCurrentFields(values, errors)
                                            }
                                            <Input
                                                type="text"
                                                value={values.name}
                                                infoText={text.resource_name_info}
                                                placeholder={text.resource_name}
                                                name={names.name}
                                                error={errors.name}
                                                onChange={e => {
                                                    handleChange(e)
                                                    setFieldTouched(names.name, true)
                                                }}
                                                activeInfo={active === names.name}
                                                handleInfo={(state) => handleInfo(names.name, state)}
                                                inputIsDisabled={getLimitIsExceeded()}
                                            />
                                            {hasAdminAccess && teams.length > 0 && (
                                                <CustomSelect
                                                    options={teams}
                                                    name={names.team_id}
                                                    placeholder={text.team_name}
                                                    onChange={async(name, data) => {
                                                        setFieldValue(name, data)
                                                        dispatch(setSelectedTeamId(data.value))
                                                        const services = await dispatch(getServices(false, true))
                                                        data?.value && setFieldValue(names.service_id, services[0])
                                                        setTeamServices(services)
                                                    }}
                                                    isClearable
                                                    error={errors.team_id}
                                                    value={!getLimitIsExceeded() && values.team_id}
                                                    activeInfo={active === names.team_id}
                                                    infoText={text.team_name_info}
                                                    handleInfo={(state) => handleInfo(names.team_id, state)}
                                                    isDisabled={getLimitIsExceeded()}
                                                />
                                            )}
                                            <CustomSelect
                                                options={teamServices}
                                                name={names.service_id}
                                                placeholder={text.service_name}
                                                onChange={setFieldValue}
                                                isClearable
                                                error={errors.service_id}
                                                value={!getLimitIsExceeded() && values.service_id}
                                                activeInfo={active === names.service_id}
                                                infoText={text.service_name_info}
                                                handleInfo={(state) => handleInfo(names.service_id, state)}
                                                isDisabled={getLimitIsExceeded()}
                                            />
                                            <CustomSelect
                                                isMulti
                                                showLabel
                                                className="regions"
                                                placeholderAsLabel={false}
                                                name={names.regions}
                                                options={locationManager.getMainLocations()}
                                                placeholder={text.regions}
                                                onChange={setFieldValue}
                                                value={!getLimitIsExceeded() && values.regions}
                                                error={errors.regions}
                                                infoText={text.regions_info}
                                                activeInfo={active === names.regions}
                                                handleInfo={(state) => handleInfo(names.regions, state)}
                                                limit={LimitManager.getRegionLimit(usageLimits)}
                                                isDisabled={getLimitIsExceeded()}
                                            />
                                        </div>
                                        <div className="advanced-form">
                                            <div className='advanced-checkbox-wrapper'>
                                                <LargeSwitch
                                                    onChange={e => {
                                                        setFieldValue(names.headers, null)
                                                        setFieldError(names.headers, null)
                                                        setFieldValue(names.body, '')
                                                        setFieldValue(names.is_advanced, e.target.checked)
                                                    }}
                                                    checked={!!values[names.is_advanced]}
                                                    name={names.is_advanced}
                                                    label={text.use_advanced}
                                                    disabled={getLimitIsExceeded()}
                                                />
                                                <div className="advanced-checkbox-label">
                                                    {text.use_advanced}
                                                </div>
                                            </div>
                                            <CustomSelect
                                                name={names.grace_period}
                                                options={gracePeriodValues}
                                                placeholder={text.grace_period}
                                                onChange={setFieldValue}
                                                value={values.grace_period}
                                                error={errors.grace_period}
                                                infoText={text.grace_period_info}
                                                activeInfo={active === names.grace_period}
                                                handleInfo={(state) => handleInfo(names.grace_period, state)}
                                                isDisabled={getLimitIsExceeded() || !values[names.is_advanced]}
                                            />
                                            <CustomSelect
                                                name={names.recovery_period}
                                                options={recoveryPeriodValues}
                                                placeholder={text.recovery_period}
                                                onChange={setFieldValue}
                                                value={values.recovery_period}
                                                error={errors.recovery_period}
                                                infoText={text.recovery_period_info}
                                                activeInfo={active === names.recovery_period}
                                                handleInfo={(state) => handleInfo(names.recovery_period, state)}
                                                isDisabled={getLimitIsExceeded() || !values[names.is_advanced]}
                                            />
                                            {isHttpMonitoring(values) && (
                                                <>
                                                    {generateMethods(values, handleChange, setFieldValue, errors)}
                                                    <AuthMethods
                                                        values={values}
                                                        handleChange={handleChange}
                                                        setFieldValue={setFieldValue}
                                                        errors={errors}
                                                        handleInfo={handleInfo}
                                                        active={active}
                                                        setButtonIsDisabled={setButtonIsDisabled}
                                                        initialFormValues={initialFormValues}
                                                    />
                                                    {generateJSONFields(values, handleChange, errors)}
                                                </>
                                            )}
                                            {generateMaintenanceField(values, handleChange, errors)}
                                        </div>
                                    </div>
                                    <div className='submit-button-wrapper'>
                                        <SimpleBlueButton
                                            disabled={buttonIsDisabled || getLimitIsExceeded()}
                                            type="submit"
                                        >
                                            {buttonIsDisabled && <ButtonLoader/>}
                                            <span>{text.start_monitoring}</span>
                                        </SimpleBlueButton>
                                    </div>
                                </form>
                            </>
                        )
                    }
                    }
                </Formik>
            </div>
        </Modal>
    )
}

export default AddResourceModal
